import { Injectable } from "@angular/core";
import { Action, State, StateContext } from "@ngxs/store";
import {
  FlowsMoveAcquisitionPrivateSwitchConsumptionsViewModel,
  FlowsMoveAcquisitionPrivateSwitchService,
  PostalCodeGridAreaElementService,
  PostalCodeGridAreaElementViewModel,
} from "@vattenfall/self-service";
import { isNullable, nullify } from "@vattenfall/util";

import { FlowsMoveAcquisitionPrivateSwitchAction } from "./flows-move-acquisition-private-switch.actions";
import { FlowsMoveAcquisitionPrivateSwitchModel } from "./flows-move-acquisition-private-switch.model";

@State<FlowsMoveAcquisitionPrivateSwitchModel>({
  name: "flowsMoveAcquisitionPrivateSwitch",
})
@Injectable()
export class FlowsMoveAcquisitionPrivateSwitchState {
  constructor(
    private postalCodeGridAreaElementService: PostalCodeGridAreaElementService,
    private flowsMoveAcquisitionPrivateSwitchService: FlowsMoveAcquisitionPrivateSwitchService
  ) {}

  @Action(FlowsMoveAcquisitionPrivateSwitchAction.Loading)
  loading(
    { patchState }: StateContext<FlowsMoveAcquisitionPrivateSwitchModel>,
    { state }: FlowsMoveAcquisitionPrivateSwitchAction.Loading
  ) {
    patchState({ state, error: undefined });
  }

  @Action(FlowsMoveAcquisitionPrivateSwitchAction.Fetch.PostalCode)
  async fetchPostalCode(
    { dispatch, patchState, setState }: StateContext<FlowsMoveAcquisitionPrivateSwitchModel>,
    { postalCode }: FlowsMoveAcquisitionPrivateSwitchAction.Fetch.PostalCode
  ) {
    try {
      dispatch(new FlowsMoveAcquisitionPrivateSwitchAction.Loading("loading-postalcode"));

      const payload: PostalCodeGridAreaElementViewModel = await this.postalCodeGridAreaElementService
        .elementsPostalCodeGridAreaPostalCodesPostalCodeGet({ postalCode })
        .toPromise();

      patchState({
        state: "completed",
        isBetweenGridAreas: isNullable(payload.energyArea),
        gridArea: nullify(payload.gridArea),
        postalCode,
        ...payload,
      });
    } catch (error) {
      // If the postal code was not found then it might be because there is no
      // grid area matching this postal code or that the postal number is incorrect
      // either way, the user must in this case enter the grid area manually
      if (error.status === 404) setState({ state: "completed", isBetweenGridAreas: true, postalCode, gridArea: null });
      else dispatch(new FlowsMoveAcquisitionPrivateSwitchAction.Errorred(error, "fetch-postalcode"));
    }
  }

  // Gridarea is case sensitive to mockup api but not in actual endpoint.
  // State is updated with capital letters used by endpoint.
  // The postaltown is already set by Fetch.postalcode
  // If an error occurs when fetching energyArea, the user should be able
  // to continoue in the flow
  @Action(FlowsMoveAcquisitionPrivateSwitchAction.Fetch.GridArea)
  async fetchGridArea(
    { dispatch, patchState }: StateContext<FlowsMoveAcquisitionPrivateSwitchModel>,
    { gridArea }: FlowsMoveAcquisitionPrivateSwitchAction.Fetch.GridArea
  ) {
    try {
      dispatch(new FlowsMoveAcquisitionPrivateSwitchAction.Loading("loading-gridarea"));
      const payload: PostalCodeGridAreaElementViewModel = await this.postalCodeGridAreaElementService
        .elementsPostalCodeGridAreaGridAreasGridAreaGet({ gridArea: gridArea.toLowerCase() })
        .toPromise();
      patchState({ state: "completed", energyArea: payload.energyArea, gridArea: payload.gridArea });
    } catch (error) {
      patchState({ gridArea: "" });
      error && console.error(error);
      dispatch(new FlowsMoveAcquisitionPrivateSwitchAction.Errorred(error, "fetch-gridarea"));
    }
  }

  /**
   * Fetch approx. Annual Consumption information
   */
  @Action(FlowsMoveAcquisitionPrivateSwitchAction.Fetch.Consumption)
  async fetchConsumption(
    { dispatch, patchState }: StateContext<FlowsMoveAcquisitionPrivateSwitchModel>,
    { payload }: FlowsMoveAcquisitionPrivateSwitchAction.Fetch.Consumption
  ) {
    try {
      const { buildingType, postalCode, livingArea } = payload;
      dispatch(new FlowsMoveAcquisitionPrivateSwitchAction.Loading("loading-consumption"));
      const result: FlowsMoveAcquisitionPrivateSwitchConsumptionsViewModel =
        await this.flowsMoveAcquisitionPrivateSwitchService
          .elementsFlowsMoveAcquisitionPrivateSwitchEstimatedConsumptionBuildingTypesBuildingTypeGet({
            buildingType: buildingType as any,
            postalCode: postalCode as any,
            livingArea,
          })
          .toPromise();

      const annualEstimatedConsumption = result.estimatedConsumptions;
      patchState({
        state: "completed",
        annualEstimatedConsumption,
      });
    } catch (error) {
      patchState({ annualEstimatedConsumption: undefined });
      dispatch(new FlowsMoveAcquisitionPrivateSwitchAction.Errorred(error, "fetch-consumption"));
    }
  }

  @Action(FlowsMoveAcquisitionPrivateSwitchAction.Errorred)
  errorred(
    { patchState }: StateContext<FlowsMoveAcquisitionPrivateSwitchModel>,
    { message, error }: FlowsMoveAcquisitionPrivateSwitchAction.Errorred
  ) {
    patchState({ state: "errorred", error });
    if (message) console.error(message);
  }

  @Action(FlowsMoveAcquisitionPrivateSwitchAction.Submitted)
  submitted(
    { patchState }: StateContext<FlowsMoveAcquisitionPrivateSwitchModel>,
    { payload }: FlowsMoveAcquisitionPrivateSwitchAction.Submitted
  ) {
    const queryParams = new URLSearchParams({
      // Make sure that the already provided search params persists to the next page as well
      ...Object.fromEntries(new URLSearchParams(window.location.search) as any),
      estimatedKwhConsumption: String(payload.estimatedKwhConsumption),
      postalCode: String(payload.postalCode),
      postalTown: payload.postalTown ? String(payload.postalTown) : "",
      gridArea: String(payload.gridArea),
      energyArea: String(payload.energyArea),
    }).toString();
    patchState({ state: "submitted", ...payload });
    window.location.href = `${payload.redirectUrl}?${queryParams}`;
  }

  @Action(FlowsMoveAcquisitionPrivateSwitchAction.Changed)
  changed(
    { setState }: StateContext<FlowsMoveAcquisitionPrivateSwitchModel>,
    { payload }: FlowsMoveAcquisitionPrivateSwitchAction.Changed
  ) {
    setState({ ...payload });
  }
}
